import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Search, Filter, Eye, Edit, Trash2, Download, MapPin, Plus, Settings, Users } from 'lucide-react';
import TourCard from '../components/UI/TourCard';
import Button from '../components/UI/Button';
import apiService from '../services/api';
import { Tour, TourPoint } from '../types';
import toast from 'react-hot-toast';
import TourForm from '../components/Tour/TourForm';
import TourPointManager from '../components/Tour/TourPointManager';
import { extractTourText } from '../utils/tourUtils';

const Tours: React.FC = () => {
  const [filterName, setFilterName] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterNameInput, setFilterNameInput] = useState('');
  const [filterPlaceInput, setFilterPlaceInput] = useState('');
  const [showTourForm, setShowTourForm] = useState(false);
  const [showTourPointManager, setShowTourPointManager] = useState(false);
  const [selectedTour, setSelectedTour] = useState<Tour | null>(null);
  const [editingTour, setEditingTour] = useState<Tour | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 12;

  const queryClient = useQueryClient();

  useEffect(() => {
    const nameTimer = setTimeout(() => {
      setFilterName(filterNameInput);
    }, 500); // 500ms delay

    return () => clearTimeout(nameTimer);
  }, [filterNameInput]);

  useEffect(() => {
    const placeTimer = setTimeout(() => {
      setFilterPlace(filterPlaceInput);
    }, 500); // 500ms delay

    return () => clearTimeout(placeTimer);
  }, [filterPlaceInput]);

  const { data: toursData, isLoading, error, refetch } = useQuery(
    ['tours', filterName, filterPlace, currentPage],
    () => apiService.getTours(filterName, filterPlace, currentPage, rowsPerPage)
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterPlace]);

  const createTourMutation = useMutation(
    (tourData: Partial<Tour>) => apiService.createTour(tourData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tours']);
        toast.success('Tour created successfully');
        setShowTourForm(false);
        setEditingTour(null);
        setCurrentPage(1);
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to create tour');
      },
    }
  );

  const updateTourMutation = useMutation(
    ({ id, data }: { id: number; data: Partial<Tour> }) => apiService.updateTour(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tours']);
        toast.success('Tour updated successfully');
        setShowTourForm(false);
        setEditingTour(null);
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to update tour');
      },
    }
  );

  const deleteTourMutation = useMutation(
    (tourId: number) => apiService.deleteTour(tourId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tours']);
        toast.success('Tour deleted successfully');
        if (toursData?.data && toursData.data.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      },
      onError: (error: any) => {
        toast.error(error.message || 'Failed to delete tour');
      },
    }
  );

  const handleView = (tour: Tour) => {
    setSelectedTour(tour);
    setShowTourPointManager(true);
  };

  const handleEdit = (tour: Tour) => {
    setEditingTour(tour);
    setShowTourForm(true);
  };

  const handleDelete = async (tour: Tour) => {
    if (window.confirm(`Are you sure you want to delete "${extractTourText(tour.name)}"? This action cannot be undone.`)) {
      deleteTourMutation.mutate(tour.id);
    }
  };



  const handleCreateTour = () => {
    setEditingTour(null);
    setShowTourForm(true);
  };

  const handleSaveTour = (tourData: Partial<Tour>) => {
    if (editingTour) {
      updateTourMutation.mutate({ id: editingTour.id, data: tourData });
    } else {
      createTourMutation.mutate(tourData);
    }
  };

  const handleManageTourPoints = (tour: Tour) => {
    setSelectedTour(tour);
    setShowTourPointManager(true);
  };

  const tours = toursData?.data || [];
  const pagination = toursData?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalTours = pagination?.total || 0;

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load tours</p>
        <Button onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tours</h1>
          <p className="text-gray-600">Manage your tour collection with points, audio, and preview settings</p>
        </div>
        <Button onClick={handleCreateTour} disabled={!apiService.getFlavourId()} title={!apiService.getFlavourId() ? 'Please create an app first' : ''}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Tour
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by tour name..."
              value={filterNameInput}
              onChange={(e) => setFilterNameInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter by place..."
              value={filterPlaceInput}
              onChange={(e) => setFilterPlaceInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>
        </div>
      </div>

      {/* Tours Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 mt-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : tours && tours.length > 0 ? (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-3">
          {tours.map((tour) => (
            <TourCard
              key={tour.id}
              tour={tour}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onManagePoints={() => handleManageTourPoints(tour)}
            />
          ))}
        </div>
        {/* Pagination Controls */}
          {totalPages >= 1 && (
            <div className="flex justify-between items-center mt-6">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} ({totalTours} total tours)
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>

                <Button
                  variant="outline"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tours found</h3>
          <p className="text-gray-600 mb-4">
            {filterName || filterPlace 
              ? 'Try adjusting your search filters'
              : ''
            }
          </p>
        </div>
      )}

      {/* Tour Form Modal */}
      {showTourForm && (
        <TourForm
          tour={editingTour}
          onSave={handleSaveTour}
          onCancel={() => {
            setShowTourForm(false);
            setEditingTour(null);
          }}
          isLoading={createTourMutation.isLoading || updateTourMutation.isLoading}
        />
      )}

      {/* Tour Point Manager Modal */}
      {showTourPointManager && selectedTour && (
        <TourPointManager
          tour={selectedTour}
          onClose={() => {
            setShowTourPointManager(false);
            setSelectedTour(null);
          }}
        />
      )}
    </div>
  );
};

export default Tours; 